//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { Api, LocalDictionaryFactory } from '../api/api.module'
import { ColumnModel } from '../api/api.models'

import { LoggerService } from '../common/common.module'
import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { GridTypes, GridController, ButtonsOptions } from '../common/grid/grid.controller'
import { DetailsOptions } from '../common/details-popup/details-popup.directive'

import { GridService } from '../../services/grid'

import { LocalizationHelper, TemplateHelper, ConfigHelper, CommonHelper, EventsNames, IUserSession } from '../../helpers'

import { Enums, Activity } from '../../models/common/'
import { OpOperatorGroup } from '../../models/core/op-operator-group'

import { OperatorGroupHelper } from './operator-group-helper'
import { SystemService } from '../../services/system'

export interface OperatorGroupScope extends ScopeBaseGrid {

    deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }
}

export class OperatorGroupController extends BaseGridController {

    // #region Consts

    private get OPERATOR_GROUP_GRID_NAME() { return 'OperatorGroup' }

    // #endregion

    // #region Members

    private grid: GridController<OpOperatorGroup>

    // #endregion


    // #region Ctor

    constructor(protected $scope: OperatorGroupScope,
        protected $state: StateService,
        $timeout: ng.ITimeoutService,
        protected api: Api,
        private logger: LoggerService,
        protected localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        private gridService: GridService,
        protected systemService: SystemService,
        private moduleName: Enums.SimaxModuleName,
        protected referenceType: Enums.ReferenceType
    ) {
        super($scope, $timeout, $state, userSession, systemService, api, localDictionary, null, referenceType)
        this.setConfirmationsOptions()
        this.setEvents()
        this.setMainGridOptions()
    }

    // #endregion


    // #region Events

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    private onConfirmDelete(): void {
        OperatorGroupHelper
            .deleteOperatorGroup(this.userSession.getDataProvider(), this.grid.selectedItem, this.moduleName)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onShowDetails(e: any): void {
        const detailsOptions: DetailsOptions = {
            hasPermission: true,
            idReferenceType: this.referenceType,
            moduleName: this.moduleName,
            pageItemsIds: this.grid.pageItemsIds,
            referenceObjectId: this.grid.selectedItemId
        }

        this.showDetails(e, detailsOptions)
    }

    private onEdit() {
        const selectedRows: number[] = this.dataGrid.getSelectedRowKeys()
        if (selectedRows.length == 1) {
            this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, OperatorGroupHelper.getEditInitParams(selectedRows[0]))
        } else {
            const columnCount = this.dataGrid.columnCount()
            const columns: any = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.dataGrid.columnOption(i));
            }

            this.$scope.$root.$broadcast(EventsNames.INITIALIZE_BULK_EDIT_POPUP, OperatorGroupHelper.getBulkEditInitParams(columns, angular.copy(this.grid.selectedGridItems)))
        }
    }

    private onDelete() {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onAdd(): void {
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, OperatorGroupHelper.getEditInitParams(0))
    }

    private onInitialized(e: any, grid: GridController<OpOperatorGroup>): void {
        this.grid = grid
        this.dataGrid = e.component

        this.setGridId(e.tableId)
    }

    // #endregion


    // #region Setup

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_GROUP_ADD) ? "['" + Activity[Activity.OPERATOR_GROUP_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_GROUP_DEL) ? "['" + Activity[Activity.OPERATOR_GROUP_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_GROUP_EDIT) ? "['" + Activity[Activity.OPERATOR_GROUP_EDIT] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_GROUP_LIST) ? "['" + Activity[Activity.OPERATOR_GROUP_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onDeleteClick: () => this.onDelete(),
            onEditClick: () => this.onEdit(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private setMainGridOptions(): void {
        this.gridService
            .getColumnModelByCategory([
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
            ], this.moduleName)
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    referenceId: Enums.ReferenceType.IdOperatorGroup,
                    idReferenceType: Enums.ReferenceType.IdOperatorGroup,
                    type: GridTypes.MAIN,
                    name: this.OPERATOR_GROUP_GRID_NAME,
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    dataSourceAdditionalFields: null,
                    defaultColumnsDictionary: defaultColumns,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.operatorGroupSaved,
                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OperatorGroupModule),

                    getDataSourceMethod: this.userSession
                        .getDataProvider().OperatorGroup.getOperatorGroupFilter,

                    onInitialized: (e: any, grid: GridController<OpOperatorGroup>) => this.onInitialized(e, grid),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onRowDoubleClick: (e: any) => this.onShowDetails(e),
                    contextMenuItems: () => this.getContextMenuItems(),

                }
            })
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items: any[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_GROUP_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSession),

                onItemClick: (e: any) => this.onEdit(),

            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items);
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )

        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    // #endregion

}
